import { useEffect } from "react";

// Router
import { useHistory } from "react-router-dom";

// Redux
import { useSelector, useDispatch } from "react-redux";
import * as actionTypes from "../../redux/actions/actionTypes";
import { authAjax, updateAuthenticated } from "../../redux/actions/auth";
import { RootState } from "../../redux/store";

import useLandingRoute from "../../custom-hooks/useLandingRoute";

// UI Components
import toast, { Toaster } from "react-hot-toast";
import { SuccessLoader } from "../../UI/Loaders/Loaders";

// Child Components
import LoginForm from "../LoginForm/LoginForm";

// Styles
import "./Login.css";
import Enter2FACode from "../../modals/Modals/Enter2FACode/Enter2FACode";

type TLoginProps = {
  username: string;
  password: string;
  code?: string;
};

const Login = () => {
  const { loading, error, success, loggedInUser } = useSelector(
    (state: RootState) => state?.authReducer,
  );

  const { getLandingRoute } = useLandingRoute();

  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = new URLSearchParams(window.location.search);
  const mfaSetupSuccess = queryParams.get("mfaSetupSuccess");

  const authHandler = (loginData: TLoginProps) => {
    dispatch(authAjax(loginData));
  };

  // Redirect only after a real login success (AUTH_SUCCESS sets success: true)
  useEffect(() => {
    if (!success) return;

    let route: string;
    if (mfaSetupSuccess) {
      route = "/login";
    } else if (loggedInUser?.mfaSetupRequired) {
      route = "/enable-2fa";
    } else {
      route = getLandingRoute();
    }

    const timer = setTimeout(() => {
      if (mfaSetupSuccess) {
        dispatch({ type: actionTypes.AUTH_LOGOUT });
      } else {
        dispatch(updateAuthenticated(true));
      }
      history.push(route);
    }, 2000);
    return () => clearTimeout(timer);
  }, [success]);

  // Show error notification for plain login failures
  useEffect(() => {
    if (!error) return;

    const isMfaRequired = error?.message?.toLowerCase() === "mfa is required";
    const isExpiredPassword =
      error?.error_code === 401 &&
      error?.message?.toLowerCase() ===
        "password has expired. please proceed to change your password";

    if (isExpiredPassword) {
      history.push("/forgot-password");
      return;
    }

    if (!isMfaRequired) {
      const isNetworkError = !error.error_code;
      toast.error(
        isNetworkError
          ? "Unable to connect to Advancly services."
          : error?.message || "Wrong login credentials",
        { id: "login-error" },
      );
    }
  }, [error]);

  const isMfaRequired =
    error?.error_code === 401 &&
    error?.message?.toLowerCase() === "mfa is required";

  let renderedComponent = null;

  if (isMfaRequired) {
    renderedComponent = <Enter2FACode onAuth={authHandler} />;
  } else if (success) {
    renderedComponent = (
      <div className="tfx-user-form-wrapper box-effect full-border white-bg animated fadeInRight">
        <SuccessLoader
          text="Logged In Successfully"
          subText="You will be redirected to your dashboard shortly."
        />
      </div>
    );
  } else {
    renderedComponent = (
      <div className="adv-form-bg">
        <LoginForm onAuth={authHandler} loading={loading} />
      </div>
    );
  }

  return (
    <div className="Login center-container  normal-1x zoomIn">
      <Toaster position="top-center" />
      <div className="center-container-wrapper">
        <div className="">{renderedComponent}</div>
      </div>
    </div>
  );
};

export default Login;
